<template>
  <div>
    <DataTable
      ref="dataTable"
      :columns="columnList"
      defaultColumnOrderDir="ASC"
      :defaultColumnOrderIndex="0"
      :url="dataURL"
      :filterOptions="filterOptions"
      :hasDateRangeFilter="true"
      exportTitle="Purchase Orders">
      <template v-slot:title>
        <slot name="title" />
      </template>
      <template v-slot:tools>
        <slot name="tools" />
      </template>
      <template v-slot:tableHeaderCell="tableHeaderCell">
        <span v-if="tableHeaderCell.data == addRunsheetColumnIndex && selectionEnabled" class="ml-2">
          <Tooltip label="Select purchase orders to collect items ready for pickup" helpStyle><i class="far fa-info-circle"></i></Tooltip>
        </span>
        <span v-if="tableHeaderCell.data == addDespatchColumnIndex && selectionEnabled" class="ml-2">
          <Tooltip label="Select purchase orders to also despatch those items where applicable" helpStyle><i class="far fa-info-circle"></i></Tooltip>
        </span>
      </template>
      <template v-slot:row="row">
        <tr :key="row.data.id">
          <td class="table-data-nav-link">
            <LinkPurchaseOrder :name="row.data.sequenceNumber ? '#'+row.data.sequenceNumber : 'Pending'" :id="row.data.id" :newTab="openNewTab" />
          </td>
          <td>
            {{row.data.contactName}}
          </td>
          <td>
            <LabelBadge :labelClass="`label-po-type-${row.data.type}`">{{row.data.type | formatLookupName(PurchaseOrderTypeLookup)}}</LabelBadge>
          </td>
          <td>
            <LabelBadge :labelClass="`label-po-status-${row.data.status}`">{{row.data.status | formatLookupName(PurchaseOrderStatusTypeLookup)}}</LabelBadge>
            <LabelBadge class="label-order-tasks-assigned ml-1" v-if="row.data.isActiveTasksAssigned"><i class="far fa-fw fa-calendar-check"></i> Tasks Assigned</LabelBadge>
          </td>
          <td>
            <FormButton class="btn-default btn-xxs" @click="viewPurchaseOrderSummary($event, row.data.id)">View</FormButton>
          </td>
          
          <td class="text-right" v-if="selectionEnabled">
            <FormCheckbox :value="isPickupPurchaseOrderSelected(row.data.id)" @change="updatePickupPurchaseOrderSelection(row.data.id, $event)" />
          </td>
          <td class="text-right" v-if="selectionEnabled">
            <FormCheckbox v-if="isDespatchPOAvailable(row.data)" :value="isDespatchPurchaseOrderSelected(row.data.id)" @change="updateDespatchPurchaseOrderSelection(row.data.id, $event)" />
            <span v-else class="text-muted">Despatch Unavailable</span>
          </td>
        </tr>
      </template>
    </DataTable>

    <PurchaseOrderSummaryPopover ref="poSummaryPopover" />
  </div>
</template>

<script>
import _ from 'lodash'
import Utils from '@/utils/utils'
import AccountLocationDisplay from '@/modules/admin/components/accountLocationDisplay'
import PurchaseOrderSummaryPopover from '@/modules/purchasing/components/purchaseOrderSummaryPopover'

import { DespatchTypes, PurchaseOrderTypes, PurchaseOrderTypeLookup, PurchaseOrderStatusTypes, PurchaseOrderStatusTypeLookup } from '@/store/types'

export default {
  name: 'TaskPurchaseOrderPickupDataTable',
  model: {
    event: 'update'
  },
  components: {
    AccountLocationDisplay,
    PurchaseOrderSummaryPopover
  },
  props: {
    openNewTab: {
      type: Boolean,
      default: false
    },
    despatchTaskTypeId: Number,
    pickupPOList: Array,
    despatchPOList: Array,
    filterAccountLocationId: Number,
    selectionEnabled: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    title() {
      return this.$route.meta.title
    },
    dataURL() {
      var params = {
        filter: {
          despatchType: DespatchTypes.pickup
        }
      }
      if(this.filterAccountLocationId)
        params.filter.accountLocationId = this.filterAccountLocationId

      return '/purchasing/purchaseorders?' + $.param(params)
    },
    addRunsheetColumnIndex() {
      return 5
    },
    addDespatchColumnIndex() {
      return 6
    }
  },
  data() {
    let columnList = _.compact([
        { title: "Number", data: "sequenceNumber" },
        { title: "Contact", data: "contact.name" },
        { title: "Type", data: "type" },
        { title: "Status", data: "status" },
        { title: "Info", data: "", sortable: false, export: false },
        this.selectionEnabled ? { title: "Add Pickup", data: "", sortable: false, export: false, class: "text-right" } : null,
        this.selectionEnabled ? { title: "Add Despatch", data: "", sortable: false, export: false, class: "text-right" } : null,
    ])

    let filterOptions = [
      {
        field: 'status',
        name: 'Status',
        options: [
          { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.pendingApproval), value: PurchaseOrderStatusTypes.pendingApproval, class: 'label-po-status-pendingApproval' },
          { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.pendingConfirmation), value: PurchaseOrderStatusTypes.pendingConfirmation, class: 'label-po-status-pendingConfirmation' },
          { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.pendingShipping), value: PurchaseOrderStatusTypes.pendingShipping, class: 'label-po-status-pendingShipping' },
          { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.pendingReceival), value: PurchaseOrderStatusTypes.pendingReceival, class: 'label-po-status-pendingReceival' },
          { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.receiving), value: PurchaseOrderStatusTypes.receiving, class: 'label-po-status-receiving' },
          { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.pendingPickup), value: PurchaseOrderStatusTypes.pendingPickup, class: 'label-po-status-pendingPickup' },
          { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.completed), value: PurchaseOrderStatusTypes.completed, class: 'label-po-status-completed' },
          { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.cancelled), value: PurchaseOrderStatusTypes.cancelled, class: 'label-po-status-cancelled' }
        ]
      },
      {
        field: 'isActiveTasksAssigned',
        name: 'Tasks Assigned',
        columnIndex: 0,
        options: [
          { name: 'Tasks Assigned', value: '1', class: 'label-order-tasks-assigned' },
          { name: 'No Tasks Assigned', value: '0', class: 'label-order-tasks-noneassigned' }
        ]
      },
    ]    

    return {
      filterOptions: filterOptions,
      columnList: columnList,
      selectedPickupPOMap: {},
      selectedDespatchPOMap: {},      
    }
  },
  watch: {
    pickupPOList: function() {
      this.updateSelectedPickupPurchaseOrderMap()
    },
    despatchPOList: function() {
      this.updateSelectedDespatchPurchaseOrderMap()
    }
  },
  created() {
    this.PurchaseOrderTypeLookup = PurchaseOrderTypeLookup
    this.PurchaseOrderStatusTypeLookup = PurchaseOrderStatusTypeLookup
  },
  mounted() {
    this.updateSelectedPickupPurchaseOrderMap()
    this.updateSelectedDespatchPurchaseOrderMap()

    // Apply default filters
    this.$nextTick(() => {
      let defaultFilters = _.compact([
        { value: PurchaseOrderStatusTypes.pendingPickup, field: 'status' },
        { value: '0', field: 'isActiveTasksAssigned' }
      ])

      this.$refs.dataTable.applySelectedFilters(defaultFilters)
    })
  },
  methods: {
    isPickupPurchaseOrderSelected(purchaseOrderId) {
      return this.selectedPickupPOMap[purchaseOrderId]
    },
    updatePickupPurchaseOrderSelection(purchaseOrderId, selected) {
      this.selectedPickupPOMap[purchaseOrderId] = selected
      this.updatePickupPurchaseOrderList()

      // If unselected, unselect despatch too
      if(!selected && this.selectedDespatchPOMap[purchaseOrderId]) {
        this.selectedDespatchPOMap[purchaseOrderId] = false
        this.updateDespatchPurchaseOrderList()
      }
    },
    isDespatchPurchaseOrderSelected(purchaseOrderId) {
      return this.selectedDespatchPOMap[purchaseOrderId]
    },
    updateDespatchPurchaseOrderSelection(purchaseOrderId, selected) {
      this.selectedDespatchPOMap[purchaseOrderId] = selected
      this.updateDespatchPurchaseOrderList()

      // If selected, force pickup too
      if(selected && !this.selectedPickupPOMap[purchaseOrderId]) {
        this.selectedPickupPOMap[purchaseOrderId] = true
        this.updatePickupPurchaseOrderList()
      }
    },
    updateSelectedPickupPurchaseOrderMap() {
      this.selectedPickupPOMap = {}
      if(this.pickupPOList) {
        _.uniq(this.pickupPOList).forEach(purchaseOrderId => {
          this.selectedPickupPOMap[purchaseOrderId] = true
        })
      }
    },
    updateSelectedDespatchPurchaseOrderMap() {
      this.selectedDespatchPOMap = {}
      if(this.despatchPOList) {
        _.uniq(this.despatchPOList).forEach(purchaseOrderId => {
          this.selectedDespatchPOMap[purchaseOrderId] = true
        })
      }
    },
    updatePickupPurchaseOrderList() {
      this.$emit('updatePickupList', Object.keys(this.selectedPickupPOMap).filter(purchaseOrderId => this.selectedPickupPOMap[purchaseOrderId]).map(purchaseOrderId => parseInt(purchaseOrderId)))
    },
    updateDespatchPurchaseOrderList() {
      this.$emit('updateDespatchList', Object.keys(this.selectedDespatchPOMap).filter(purchaseOrderId => this.selectedDespatchPOMap[purchaseOrderId]).map(purchaseOrderId => parseInt(purchaseOrderId)))
    },
    isDespatchPOAvailable(purchaseOrder) {
      if(purchaseOrder.order && purchaseOrder.order.despatchTaskTypeId == this.despatchTaskTypeId)
        return true
      return false
    },
    viewPurchaseOrderSummary(ev, purchaseOrderId) {
      this.$refs.poSummaryPopover.displayFromElement(ev.target, purchaseOrderId)
    }
  }
};
</script>
